import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatMenu } from '@angular/material/menu';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SgMenuGroup, SgMenuItem, SgMenuItemEvent, SgMenuToggleEvent } from './sg-menu.model';

@Component({
  selector: 'sg-menu',
  templateUrl: './sg-menu.component.html',
  styleUrls: ['./sg-menu.component.scss'],
  standalone: false,
})
export class SgMenuComponent implements OnInit {
  //#region Properties
  @Input() items?: SgMenuItem[];
  @Input() groups?: SgMenuGroup[];
  @Input() multiSelect = false;
  @Input() hasBackdrop = true;

  @Output() itemSelect = new EventEmitter<SgMenuItemEvent>();
  @Output() itemDeselect = new EventEmitter<SgMenuItemEvent>();
  @Output() suffixIconClick = new EventEmitter<SgMenuItemEvent>();
  @Output() toggleChange = new EventEmitter<SgMenuToggleEvent>();
  @Output() submenuOpen = new EventEmitter<SgMenuItemEvent>();

  @ViewChild('menu', { static: false }) menu!: MatMenu;

  activeSubmenus = new Set<string>();
  //#endregion

  constructor(private sanitizer: DomSanitizer) {}

  //#region Lifecycle Hooks
  ngOnInit(): void {
    this.syncToggleStates();
  }
  //#endregion

  //#region Getters
  get matMenu(): MatMenu {
    return this.menu;
  }

  get hasGroups(): boolean {
    return !!(this.groups && this.groups.length > 0);
  }

  get hasItems(): boolean {
    return !!(this.items && this.items.length > 0);
  }
  //#endregion

  //#region Public Methods
  onItemClick(item: SgMenuItem, group?: SgMenuGroup, event?: MouseEvent): void {
    const shouldPreventClose =
      this.multiSelect || item.showToggle || item.preventMenuClose || group?.preventMenuClose || group?.singleSelect;

    if (shouldPreventClose) {
      event?.stopPropagation();
    }

    if (item.isDisabled || item.readonly) {
      return;
    }

    if (item.children && item.children.length > 0) {
      this.handleSubmenuToggle(item, group);
      return;
    }

    if (item.showToggle) {
      const newToggleValue = !item.toggleValue;
      item.toggleValue = newToggleValue;
      item.isSelected = newToggleValue;

      this.toggleChange.emit({ item, toggleValue: newToggleValue, group });

      if (newToggleValue) {
        this.itemSelect.emit({ item, group });
      } else {
        this.itemDeselect.emit({ item, group });
      }
      return;
    }

    if (item.action) {
      item.action();
    }

    this.handleSelection(item, group);
  }

  onSuffixIconClick(event: MouseEvent, item: SgMenuItem, group?: SgMenuGroup): void {
    event.stopPropagation();

    if (item.isDisabled || item.readonly) {
      return;
    }

    this.suffixIconClick.emit({ item, group });
  }

  onToggleChange(toggleValue: boolean, item: SgMenuItem, group?: SgMenuGroup): void {
    if (item.isDisabled) {
      return;
    }

    item.toggleValue = toggleValue;
    item.isSelected = toggleValue;

    this.toggleChange.emit({ item, toggleValue, group });

    if (toggleValue) {
      this.itemSelect.emit({ item, group });
    } else {
      this.itemDeselect.emit({ item, group });
    }
  }

  getSuffixIcon(item: SgMenuItem, group?: SgMenuGroup): string {
    if (item.isSelected && (this.multiSelect || group?.singleSelect || item.showToggle)) {
      return 'check';
    }

    if (item.suffixIcon && (!item.children || item.children.length === 0)) {
      return item.suffixIcon;
    }

    return '';
  }

  trackByItemId(index: number, item: SgMenuItem): string {
    return item.id;
  }

  trackByGroupId(index: number, group: SgMenuGroup): string {
    return group.id;
  }

  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
  //#endregion

  //#region Private Methods
  private syncToggleStates(): void {
    if (this.hasGroups) {
      this.groups?.forEach((group) => {
        group.items.forEach((item) => {
          if (item.showToggle) {
            item.isSelected = !!item.toggleValue;
          }
        });
      });
    } else {
      this.items?.forEach((item) => {
        if (item.showToggle) {
          item.isSelected = !!item.toggleValue;
        }
      });
    }
  }

  private handleSelection(item: SgMenuItem, group?: SgMenuGroup): void {
    if (group?.singleSelect) {
      group.items.forEach((groupItem) => {
        if (groupItem.id !== item.id) {
          groupItem.isSelected = false;
        }
      });
      item.isSelected = true;
      this.itemSelect.emit({ item, group });
    } else if (this.multiSelect) {
      const wasSelected = item.isSelected;
      item.isSelected = !wasSelected;

      if (item.isSelected) {
        this.itemSelect.emit({ item, group });
      } else {
        this.itemDeselect.emit({ item, group });
      }
    } else {
      this.clearAllSelections();
      item.isSelected = true;
      this.itemSelect.emit({ item, group });
    }
  }

  private clearAllSelections(): void {
    if (this.hasGroups) {
      this.groups?.forEach((group) => {
        group.items.forEach((item) => (item.isSelected = false));
      });
    } else {
      this.items?.forEach((item) => (item.isSelected = false));
    }
  }

  private handleSubmenuToggle(item: SgMenuItem, group?: SgMenuGroup): void {
    if (this.activeSubmenus.has(item.id)) {
      this.activeSubmenus.delete(item.id);
    } else {
      this.activeSubmenus.add(item.id);
    }

    this.submenuOpen.emit({ item, group });
  }
  //#endregion
}
